import { Client, IMessage } from '@stomp/stompjs';
import { useNotificationStore } from '../store/notification.store';
import { useAuthStore } from '../store/auth.store';
import { safeLocalStorage } from '../utils/safeStorage';

// Always use relative URL. Start with relative path /ws.
// This avoids DNS issues by letting Nginx proxy the request to the backend via ALB
// using the same domain as the frontend (www.bricolat.co.ma/ws)
const getWebSocketUrl = (): string => {
    return '/ws';
};

let stompClient: Client | null = null;
let isConnecting = false; // Prevent duplicate connection attempts
let connectAttempt = 0; // Invalidates a pending connect() when disconnect() is called meanwhile

export const websocketService = {
    connect() {
        // Get token from localStorage (where authService stores it)
        const token = safeLocalStorage.getItem('token');
        const userId = useAuthStore.getState().user?.id;

        if (!token || !userId) {
            return;
        }

        // Prevent duplicate connections
        if (stompClient?.connected || isConnecting) {
            return;
        }

        isConnecting = true;

        const attempt = ++connectAttempt;
        // sockjs-client registers an `unload` listener (deprecated, blocks the back/forward cache): it is loaded
        // only here, for an authenticated realtime connection, never for an anonymous page view.
        void import('sockjs-client').then(({ default: SockJS }) => {
            if (attempt !== connectAttempt) return; // disconnected while the module was loading
            // Use SockJS for fallback support
            const socketUrl = getWebSocketUrl();
            stompClient = new Client({
                webSocketFactory: () => new SockJS(socketUrl),
                connectHeaders: {
                    Authorization: `Bearer ${token}`,
                },
                // STOMP debug frames include the Authorization header. Never log them.
                debug: () => undefined,
                reconnectDelay: 5000,
                heartbeatIncoming: 4000,
                heartbeatOutgoing: 4000,
            });

            stompClient.onConnect = () => {
                isConnecting = false;
                useNotificationStore.getState().setConnected(true);

                // Subscribe to user-specific notifications
                // Note: Spring resolves /user/queue/... to the authenticated user automatically
                stompClient?.subscribe('/user/queue/notifications', (message: IMessage) => {
                    try {
                        const notification = JSON.parse(message.body);
                        useNotificationStore.getState().addNotification(notification);

                        // Play notification sound (optional)
                        playNotificationSound();
                    } catch (error) {
                        console.error('Failed to parse notification:', error);
                    }
                });

                // Subscribe to unread count updates
                stompClient?.subscribe('/user/queue/notifications/count', (message: IMessage) => {
                    try {
                        const { unreadCount } = JSON.parse(message.body);
                        useNotificationStore.getState().setUnreadCount(unreadCount);
                    } catch (error) {
                        console.error('Failed to parse unread count:', error);
                    }
                });
            };

            stompClient.onDisconnect = () => {
                isConnecting = false;
                useNotificationStore.getState().setConnected(false);
            };

            stompClient.onStompError = (frame) => {
                console.error('WebSocket STOMP error:', frame.headers['message']);
                isConnecting = false;
                useNotificationStore.getState().setConnected(false);
            };

            stompClient.activate();
        }).catch(() => {
            isConnecting = false;
        });
    },

    disconnect() {
        connectAttempt += 1;
        isConnecting = false;
        if (stompClient) {
            stompClient.deactivate();
            stompClient = null;
            isConnecting = false;
            useNotificationStore.getState().setConnected(false);
        }
    },

    isConnected(): boolean {
        return stompClient?.connected ?? false;
    },

    /**
     * Subscribe to a topic and call callback when messages arrive.
     * Returns an unsubscribe function.
     */
    subscribeToTopic(topic: string, callback: (message: any) => void): () => void {
        if (!stompClient?.connected) {
            return () => { };
        }

        const subscription = stompClient.subscribe(topic, (message: IMessage) => {
            try {
                const data = JSON.parse(message.body);
                callback(data);
            } catch (error) {
                console.error('WebSocket: Failed to parse topic message:', error);
            }
        });

        // Return unsubscribe function
        return () => {
            subscription.unsubscribe();
        };
    },

    /**
     * Get the STOMP client instance (for components that need direct access)
     */
    getClient(): Client | null {
        return stompClient;
    },
};

function playNotificationSound() {
    try {
        // Create a simple notification sound using Web Audio API
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = 800;
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
        // Silently fail if audio context is not available
    }
}
